import {
    pxToRem,
    useAppDisplayModeContext,
    useStyles,
} from '@archipro-design/aria';
import React, { useState, useEffect, useRef } from 'react';
import type { Position } from '@modules/product-tagging/type';
import type { Position as Direction } from '@fluentui/react-northstar/dist/es/utils/positioner/types';
import type { TagProductType } from '@modules/product-tagging/type';
import * as S from './ProductTagsDisplay.style';
import { MessageTooltip, Flex } from '@archipro-design/aria';
import { TAG_TOOLTIP_MAX_WIDTH } from '@modules/product-tagging/components/product-tags-display/ProductTagsDisplay';
import { useNavigate } from 'react-router';
import { useLogImpression, useTracker } from '@archipro-website/tracker';
import { useUser } from '@modules/user';

interface ProductTagIndicatorProps {
    tagPosition: Position;
    tooltipDirection: Direction;
    taggedProduct: TagProductType;
    revealDots: boolean;
    revealTags: boolean;
    forceReveal: boolean;
}

export const INITIAL_TAG_REVEAL_TIMEOUT = 3000;

const ProductTagIndicator = ({
    tagPosition,
    tooltipDirection,
    taggedProduct,
    revealDots,
    revealTags,
    forceReveal,
}: ProductTagIndicatorProps) => {
    const triggerRef = useRef<HTMLDivElement>(null);
    const user = useUser();
    useLogImpression({
        data: {
            professionalID: taggedProduct.professionalID ?? 0,
            itemID: taggedProduct.id,
            type:
                user.__typename === 'Me'
                    ? 'ProductTagLoggedIn'
                    : 'ProductTagLoggedOut',
        },
        ref: triggerRef,
    });

    const { mobile } = useAppDisplayModeContext();

    // dot and tooltip animates into each other
    // in any instance if the tooltip is shown the dot is hidden and vice versa
    const [tooltipVisible, setTooltipVisible] = useState(revealTags);
    const [dotVisible, setDotVisible] = useState(revealDots);
    const [initialTagsRevealed, setInitialTagsRevealed] = useState(false);
    const { css } = useStyles({
        tagPosition,
        tooltipVisible: forceReveal || tooltipVisible,
        tooltipDirection,
        dotVisible,
        forceReveal,
    });
    const navigate = useNavigate();
    const tracker = useTracker();

    // auto reveal
    useEffect(() => {
        setTooltipVisible(revealTags);
        setInitialTagsRevealed(false);
    }, [revealTags]);
    useEffect(() => {
        setDotVisible(revealDots);
        setInitialTagsRevealed(false);
    }, [revealDots]);

    // auto hide
    useEffect(() => {
        if (!initialTagsRevealed) {
            const timeout = setTimeout(() => {
                if (mobile) {
                    setDotVisible(false);
                } else {
                    setTooltipVisible(false);
                }
                setInitialTagsRevealed(true);
            }, INITIAL_TAG_REVEAL_TIMEOUT);

            return () => clearTimeout(timeout);
        }
    }, [initialTagsRevealed, mobile]);

    const onClick = () => {
        tracker.log('TagHoverClick', {
            url: new URL(window.location.href),
            targetTracker: ['archiproTracker', 'ga4Tracker'],
            data: {
                ExtraData: taggedProduct.id,
            },
            ga4Data: { tagId: taggedProduct.id },
        });

        navigate(taggedProduct.link);
    };

    return (
        <MessageTooltip
            trigger={
                <div ref={triggerRef} className={css(S.TagIndicatorHoverArea)}>
                    <div className={css(S.TagIndicator)} />
                </div>
            }
            content={{
                children: (
                    <div className={css(S.TagTooltipBody)} onClick={onClick}>
                        {taggedProduct.title}
                        <Flex>
                            <div className={css(S.ProfessionalTitle)}>
                                {taggedProduct.professional}
                            </div>
                            <div className={css(S.ProductPrice)}>
                                {taggedProduct.price || ''}
                            </div>
                        </Flex>
                    </div>
                ),
                className: css(S.TagTooltip),
                size: 16,
                pointing: true,
                variables: {
                    maxWidth: pxToRem(TAG_TOOLTIP_MAX_WIDTH),
                },
            }}
            position={tooltipDirection}
            unstable_pinned={true}
            align={'center'}
            offset={[
                0,
                tooltipDirection === 'above' || tooltipDirection === 'below'
                    ? -8
                    : -7,
            ]}
            onOpenChange={(state) => {
                const entering = state.type === 'mouseenter';
                if (state.target === triggerRef.current) {
                    setTooltipVisible(entering);
                    setInitialTagsRevealed(true);
                    if (entering) {
                        tracker.log('TagHoverReveal', {
                            url: new URL(window.location.href),
                            targetTracker: ['archiproTracker', 'ga4Tracker'],
                            data: {
                                ExtraData: taggedProduct.id,
                            },
                            ga4Data: { tagId: taggedProduct.id },
                        });
                    }
                } else if (!entering) {
                    setTooltipVisible(false);
                }
            }}
            open={!mobile} //always open so we can animate this on desktop.
        />
    );
};

export default ProductTagIndicator;
